import React from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import {
  IColumnsSettingsConfig,
  ICustomBottomToolbarProps,
  ICustomTopToolbarProps,
  IGridPagination,
  IGroupedColumns,
} from '@private/data-grid';

import { useDownloads } from 'components/Downloads/useDownloads';
import HeaderCell from 'components/Grids/DataGrid/Cells/HeaderCell';
import RowCell from 'components/Grids/DataGrid/Cells/RowCell';
import { getExpandedContentHeight } from 'components/Grids/DataGrid/helpers';
import { useGridColumnsSettings } from 'components/Grids/DataGrid/hooks/useGridColumnsSettings';
import { useGridExpand } from 'components/Grids/DataGrid/hooks/useGridExpand';
import { IDataGrid } from 'components/Grids/DataGrid/types';
import HintButton from 'components/HintButton';
import Loader from 'components/Loader';
import NoItemsFound from 'components/NoItemsFound';
import PaginationToolbar from 'components/PaginationToolbar';
import { appReactMemo } from 'hocs';
import { useBreakpoint } from 'hooks/adaptive/useBreakpoint';
import { IFilterSetting } from 'utils/types';

import { FilterContextProvider } from './Filters/FilterContext';

import { DataGridProvider, EditingCellProvider, IGridContext } from './DataGridContext';
import { DragDropLeftIcon, DragDropRightIcon, StyledDataGrid } from './styled';
import TopToolbar from './TopToolbar';

const testIds = {
  contentContainer: 'gridContentContainer',
};

const renderColumnHint = (hintText: string) => <HintButton>{hintText}</HintButton>;

const DataGrid = <T,>({
  columns,
  data,
  downloadReportKey,
  filters,
  hasEditableCells,
  isFetching,
  isLoading,
  onDownloadReport,
  refetch,
  searchString,
  sort,
  total,

  baseOffColumns,
  baseOnColumns,
  columnsSettingsConfig,
  components = {},
  customUpperTopToolbar,
  filtersConfig,
  notVirtualized = false,
  pageFiltersConfig,
  pagination = {},

  defaultMinWidthColumn = 150,
  enableColumnStretch = true,
  headerHeight = 60,
  isBottomToolbar = true,
  isDefaultGridSorting,
  isExpandable = false,
  isFilter = true,
  isPagination = true,
  isTopToolbar = true,
  rowHeight = () => 60,
  withCustomTopRightColumn,

  ...rest
}: IDataGrid<T>) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  const checkWindowWidth = useBreakpoint();

  const [gridEditMode, setGridEditMode] = React.useState(false);

  const gridRowHeight = React.useMemo(
    () => (checkWindowWidth(1000) ? () => 55 : rowHeight),
    [checkWindowWidth],
  );

  const gridHeaderHeight = React.useMemo(
    () => (checkWindowWidth(1000) ? 55 : headerHeight),
    [checkWindowWidth],
  );

  const groupedColumnsCastRef = React.useRef<IGroupedColumns<T>>({
    center: [],
    left: [],
    right: [],
  });

  const { canCreateReport, createReport } = useDownloads(downloadReportKey);

  const {
    isRedirectedPreset,
    onPageChange,
    onPerPageChange,
    onSortChange,
    pageFilters,
    pageKey,
  } = pageFiltersConfig || {};

  // Expand state lives in the user-config query cache, so deriving it here from
  // pageKey stays in sync with any ExpandTableSwitch reading the same key
  const { isExpanded } = useGridExpand(isExpandable ? pageKey : undefined);

  const {
    gridColumnsSettings,
    isGridColumnSettingExpanded,
    onColumnSettingsExpansionChange,
    onColumnStretchChange,
    onColumnVisibilityChange,
    onOrderChange,
  } = useGridColumnsSettings({
    baseOffColumns,
    baseOnColumns,
    columns,
    ignoreList: filtersConfig?.ignoreList,
    isRedirectedPreset,
    pageFilters,
    pageKey,
  });

  const columnsSettings = React.useMemo(
    () =>
      ({
        ...columnsSettingsConfig,
        columnSettingsExpanded: isMobile ? undefined : isGridColumnSettingExpanded,
        emptyFiltersSearchComponent: (
          <NoItemsFound
            subtitle={t('useAnotherFilter')}
            title={t('noMatchingItemsFound')}
            titleSize='md'
            width={130}
          />
        ),
        enableColumnStretch,
        enableDragColumns: true,
        enableSearch: true,
        initialColumns: gridColumnsSettings,
        initialOffColumns: baseOffColumns,
        initialOnColumns: baseOnColumns,
        isColumnsVisibilityFiltrationEnabled: true,
        onColumnSettingsExpansionChange,
        onOrderChange,
        onStretchChange: onColumnStretchChange,
        onVisibilityChange: onColumnVisibilityChange,
        pinnedColumns: {
          dragDropImg: {
            left: <DragDropLeftIcon />,
            right: <DragDropRightIcon />,
          },
          left: true,
          maxLeftPin: 3,
          maxRightPin: 3,
          right: true,
        },
        renderColumnHint,
        renderEmptyHeaderCell: (params) => <HeaderCell {...params} isEmpty />,
        renderEmptyRowCell: (params) => <RowCell {...params} isEmpty />,
      }) as IColumnsSettingsConfig<T>,
    [
      checkWindowWidth,
      columnsSettingsConfig,
      isGridColumnSettingExpanded,
      gridColumnsSettings,
      baseOffColumns,
      baseOnColumns,
      onColumnVisibilityChange,
      onColumnSettingsExpansionChange,
      onOrderChange,
      onColumnStretchChange,
      t,
      enableColumnStretch,
    ],
  );

  const defaultSortedColumn = React.useMemo(() => {
    const entries = Object.entries<IFilterSetting>(filters || {});
    const [defaultItem] = entries.find(([_key, filter]) => filter.defaultSorting) || [];

    return defaultItem;
  }, [filters]);

  const currentContext: IGridContext = React.useMemo(
    () => ({
      defaultSortedColumn,
      disabled: !data.length || !!isFetching,
      filters,
      gridEditMode,
      isDefaultGridSorting,
      onSort: onSortChange,
      requestedSort: pageFilters?.sort,
      setGridEditMode,
      sort,
    }),
    [
      defaultSortedColumn,
      data.length,
      isFetching,
      filters,
      gridEditMode,
      onSortChange,
      pageFilters?.sort,
      sort,
    ],
  );

  const { page: currentPage, size: rowsPerPage } = pageFilters || {};

  const expandedContentHeight = React.useMemo(
    () =>
      getExpandedContentHeight({
        dataLength: data.length,
        headerHeight: gridHeaderHeight,
        isExpanded,
        rowHeight: gridRowHeight(0),
        rowsPerPage,
      }),
    [isExpanded, data.length, rowsPerPage, gridHeaderHeight, gridRowHeight],
  );

  const paginationConfig = React.useMemo(() => {
    const { isDefaultGridPagination = false } = pagination;

    return {
      currentPage,
      isDefaultGridPagination,
      onPageChange,
      onPerPageChange,
      rowsPerPage,
      startPage: 0,
      total,
    } as IGridPagination;
  }, [currentPage, rowsPerPage, pagination, onPageChange, onPerPageChange, total]);

  const textConfig = React.useMemo(
    () => ({
      bottomToolbar: {
        entries: t('pagination.entries'),
        of: t('pagination.of'),
        to: t('pagination.to'),
      },
    }),
    [t],
  );

  const NotFoundPageComponent = React.useCallback(
    ({ isEmptyColumns }: { isEmptyColumns?: boolean }) => (
      <NoItemsFound
        subtitle={isEmptyColumns ? t('allColumnsAreOff') : t('anotherFiltersCombination')}
        title={isEmptyColumns ? t('noItemsFound') : t('noItemsFound')}
      />
    ),
    [t],
  );

  const LoaderComponent = React.useCallback(() => <Loader size={70} />, []);

  const onGroupedColumnsChanges = React.useCallback(
    (groupedColumns: IGroupedColumns<T>) => {
      groupedColumnsCastRef.current = groupedColumns;
    },
    [],
  );

  const onDownloadReports = React.useCallback(() => {
    const displayedColumnNames = [
      ...groupedColumnsCastRef.current.left,
      ...groupedColumnsCastRef.current.center,
      ...groupedColumnsCastRef.current.right,
    ].map((column) => column.dataKey);

    if (onDownloadReport) {
      return onDownloadReport({ columns: displayedColumnNames, pageFilters });
    }

    createReport({
      columns: displayedColumnNames,
      pageFilters,
    });
  }, [onDownloadReport, createReport, pageFilters, groupedColumnsCastRef]);

  const TopToolbarComponent = React.useCallback(
    (props: ICustomTopToolbarProps<T>) => (
      <TopToolbar
        {...props}
        customUpperTopToolbar={customUpperTopToolbar}
        hasEditableCells={hasEditableCells}
        isExpandable={isExpandable}
        isFilterEnabled={isFilter}
        onDownloadReport={canCreateReport ? onDownloadReports : undefined}
        refetch={refetch}
        withCustomTopRightColumn={withCustomTopRightColumn}
      />
    ),
    [
      customUpperTopToolbar,
      hasEditableCells,
      isExpandable,
      isFilter,
      canCreateReport,
      onDownloadReports,
      refetch,
      withCustomTopRightColumn,
    ],
  );

  const BottomToolbarComponent = React.useCallback(
    (props: ICustomBottomToolbarProps) => (
      <PaginationToolbar {...props} disabled={!isPagination} />
    ),
    [isPagination],
  );

  const componentsConfig = React.useMemo(
    () => ({
      BottomToolbar: BottomToolbarComponent,
      Loader: LoaderComponent,
      NotFoundPage: NotFoundPageComponent,
      TopToolbar: TopToolbarComponent,
      ...components,
    }),
    [
      TopToolbarComponent,
      BottomToolbarComponent,
      LoaderComponent,
      NotFoundPageComponent,
      components,
    ],
  );

  return (
    <DataGridProvider value={currentContext}>
      <EditingCellProvider>
        <FilterContextProvider
          filtersConfig={filtersConfig}
          gridColumnsSettings={gridColumnsSettings}
          pageKey={pageKey}
          searchString={searchString}
          {...pageFiltersConfig}
        >
          {/* Remount on mode change: AutoSizer measures the container the grid renders
          into, so after collapsing it would keep re-measuring the still-tall content;
          a fresh mount starts from an empty container and settles at the default height */}
          <StyledDataGrid
            key={isExpanded ? 'expandedGrid' : 'defaultGrid'}
            {...rest}
            $expandedContentHeight={expandedContentHeight}
            columns={columns}
            columnsSettingsConfig={columnsSettings}
            components={componentsConfig}
            data={data}
            defaultMinWidthColumn={defaultMinWidthColumn}
            headerHeight={gridHeaderHeight}
            isBottomToolbar={isBottomToolbar}
            isFetching={isFetching}
            isLoading={isLoading}
            isTopToolbar={isTopToolbar}
            notVirtualized={notVirtualized}
            onChangeGroupedColumns={onGroupedColumnsChanges}
            pagination={paginationConfig}
            rowHeight={gridRowHeight}
            testIds={testIds}
            textConfig={textConfig}
          />
        </FilterContextProvider>
      </EditingCellProvider>
    </DataGridProvider>
  );
};

export default appReactMemo(DataGrid);
